import { parseUtcTimestamp } from './dateTimeFormatting';

/** Client-side mirror of the alert list query (AlertRepository) used to patch lists from SignalR events. */
export interface AlertMatchCriteria {
  /** API status filter: OpenActive | Open | UnAcked | Acked | Closed | All | undefined. */
  status?: string;
  severity?: string;
  service?: string;
  search?: string;
  assignedTo?: string;
  startUtc?: string;
  /** Omit for relative ranges ("last N hours"), whose end is always "now". */
  endUtc?: string;
}

type AlertLike = {
  alertId?: string;
  status?: string;
  severity?: string;
  service?: string;
  title?: string;
  description?: string;
  source?: string;
  assignedTo?: string | null;
  createdAt?: string;
};

export const normalizeAlertId = (value?: string | null) =>
  String(value || '').trim().replace(/[{}]/g, '').toLowerCase();

const same = (a?: string | null, b?: string | null) =>
  String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();

const contains = (value: string | undefined, term: string) =>
  String(value ?? '').toLowerCase().includes(term);

const createdAtMs = (alert: AlertLike) => {
  if (!alert.createdAt) return Number.NaN;
  return parseUtcTimestamp(alert.createdAt).getTime();
};

export function alertMatchesCriteria(alert: AlertLike, criteria: AlertMatchCriteria): boolean {
  const status = String(alert.status || '').toLowerCase();
  switch (String(criteria.status || '').trim().toLowerCase()) {
    case '':
    case 'all':
      break;
    case 'openactive':
      if (status !== 'open' && status !== 'acked') return false;
      break;
    case 'open':
    case 'unacked':
    case 'unack':
      if (status !== 'open') return false;
      break;
    default:
      if (status !== String(criteria.status).trim().toLowerCase()) return false;
  }

  if (criteria.assignedTo && normalizeAlertId(alert.assignedTo) !== normalizeAlertId(criteria.assignedTo)) return false;
  if (criteria.severity && !same(alert.severity, criteria.severity)) return false;
  if (criteria.service && !same(alert.service, criteria.service)) return false;

  const term = criteria.search?.trim().toLowerCase();
  if (term && !contains(alert.title, term) && !contains(alert.description, term) && !contains(alert.source, term)) {
    return false;
  }

  if (criteria.startUtc || criteria.endUtc) {
    const created = createdAtMs(alert);
    if (Number.isFinite(created)) {
      if (criteria.startUtc && created < parseUtcTimestamp(criteria.startUtc).getTime()) return false;
      if (criteria.endUtc && created > parseUtcTimestamp(criteria.endUtc).getTime()) return false;
    }
  }

  return true;
}

/**
 * Applies a created/updated alert to a createdAt-desc list without refetching:
 * merges in place when it still matches, removes it when it no longer matches,
 * and inserts it in order when it newly matches.
 */
export function applyAlertChange<T extends AlertLike>(
  list: T[],
  incoming: T,
  criteria: AlertMatchCriteria,
  merge: (existing: T, incoming: T) => T,
): T[] {
  const id = normalizeAlertId(incoming.alertId);
  if (!id) return list;
  const index = list.findIndex(item => normalizeAlertId(item.alertId) === id);
  const candidate = index >= 0 ? merge(list[index], incoming) : incoming;

  if (!alertMatchesCriteria(candidate, criteria)) {
    return index >= 0 ? list.filter((_, i) => i !== index) : list;
  }

  if (index >= 0) {
    const next = list.slice();
    next[index] = candidate;
    return next;
  }

  const created = createdAtMs(candidate);
  const insertAt = Number.isFinite(created)
    ? list.findIndex(item => !(createdAtMs(item) >= created))
    : 0;
  const next = list.slice();
  next.splice(insertAt < 0 ? next.length : insertAt, 0, candidate);
  return next;
}

export function removeAlertById<T extends AlertLike>(list: T[], alertId?: string | null): T[] {
  const id = normalizeAlertId(alertId);
  if (!id) return list;
  const next = list.filter(item => normalizeAlertId(item.alertId) !== id);
  return next.length === list.length ? list : next;
}
